import { ReactElement, useState } from 'react';
import * as S from './ImagesWidget.styles';
import URL from '@/common/app/utils/urlsUtils';
import { useMedia } from '@/common/service/hooks/useMedia';
import { ImagesPopup } from './ImagesPopup';
import Video from '../Video/Video';

type ImagesWidgetProps = {
    images: {
        id: string;
        slug: string;
    }[];
    path: string;
    video?: string;
};

type TImageItem = Record<'path' | 'id' | 'slug' | 'restNumber', string> & {
    onClick: () => void;
};

const PREVIEW_LENGTH_DESKTOP = 7;
const PREVIEW_LENGTH_MOBILE = 4;

export const ImageItem = ({ path, id, slug, restNumber, onClick }: TImageItem) => {
    const url = `${URL.image}/${path}/${id}/300x300/${slug}`;

    return (
        <S.ImageItem onClick={onClick}>
            <source type="image/webp" srcSet={`${url}.webp`} />
            <source type="image/jpeg" srcSet={`${url}.jpg`} />
            <img src={`${url}.jpg`} alt={slug} height="100%" width="100%" />
            {restNumber && <S.RestNumber>{`+${restNumber}`}</S.RestNumber>}
        </S.ImageItem>
    );
};

export const ImagesWidget = ({ images, path, video }: ImagesWidgetProps): ReactElement => {
    const [showAllImages, setShowAllImages] = useState(false);
    const [showPopup, setShowPopup] = useState(false);
    const [startPosition, setStartPosition] = useState(0);
    const isDesktop = useMedia('(min-width: 992px)');

    if (!images.length && !video) {
        return <></>;
    }

    const previewLength = isDesktop ? PREVIEW_LENGTH_DESKTOP : PREVIEW_LENGTH_MOBILE;
    const listLength = video ? previewLength - 1 : previewLength;
    const restNumber = images.length > listLength ? images.length - listLength : 0;

    const closePopup = () => {
        setShowAllImages(false);
        setShowPopup(false);
        setStartPosition(0);
    };

    const showSingleImage = (position?: number) => {
        setShowPopup(true);
        setShowAllImages(false);
        if (position) {
            setStartPosition(position);
        }
    };
    const showAll = () => {
        setShowPopup(true);
        setShowAllImages(true);
    };

    const indexStep = video ? 2 : 1;

    return (
        <S.Container isFull={!!restNumber}>
            {video && (
                <S.VideoItem onClick={() => showSingleImage()}>
                    <Video link={video} smallBtn preventPlay />
                </S.VideoItem>
            )}
            {images.slice(0, listLength).map(({ id, slug }, index) => {
                const showRestNumber =
                    restNumber && index === listLength - 1 ? restNumber.toString() : '';

                return (
                    <ImageItem
                        path={path}
                        id={id}
                        slug={slug}
                        key={id}
                        restNumber={showRestNumber}
                        onClick={
                            showRestNumber ? showAll : () => showSingleImage(index + indexStep)
                        }
                    />
                );
            })}
            {showPopup && (
                <ImagesPopup
                    onClose={closePopup}
                    userImages={images}
                    videos={video ? [{ link: video }] : []}
                    path={path}
                    startPosition={startPosition}
                    showAll={showAllImages}
                    showSingleImage={showSingleImage}
                />
            )}
        </S.Container>
    );
};
